import { getMyUserProfile } from '@wix/ambassador-data-services-profile-client-v4-profile-field-data/http';
import {
  GetMyUserProfileRequest,
  GetMyUserProfileResponse,
  ProfileValue,
} from '@wix/ambassador-data-services-profile-client-v4-profile-field-data/types';
import { HttpClient, HttpResponse } from '@wix/http-client';
import { isNumber } from 'lodash';

const USER_GROUPS_FIELD_NAME = 'user_groups';
const LAST_LOGIN_FIELD = 'last_login';
const SKIP_2FA_FIELD = 'skip_2fa_enforcement';

/**
 * Reads a profile timestamp as milliseconds, so we can compare it with
 * `Date.now()`.
 */
export const profileValueToMillis = (
  value?: ProfileValue,
): number | undefined => {
  if (!value) {
    return undefined;
  }
  if (value.aLong !== undefined && value.aLong !== null) {
    const millis = Number(value.aLong);
    return Number.isFinite(millis) ? millis : undefined;
  }
  if (value.aString) {
    const millis = Date.parse(value.aString);
    return Number.isNaN(millis) ? undefined : millis;
  }
  return undefined;
};

export class BiProfileServiceApi {
  constructor() {
    this.handleError = this.handleError.bind(this);
  }

  public getProfileData = async ({
    fields,
  }: {
    fields: string[];
  }): Promise<GetMyUserProfileResponse> => {
    const request: GetMyUserProfileRequest = {
      fields,
    };

    const httpClient = new HttpClient();
    let result: HttpResponse<GetMyUserProfileResponse> | null = null;
    try {
      result = await httpClient.request(getMyUserProfile(request));
    } catch (e) {
      console.error('Failed to get profile data', e);
    }
    return result!.data;
  };

  public async getUserGroups(): Promise<ProfileValue[]> {
    const result = await this.getProfileData({
      fields: [USER_GROUPS_FIELD_NAME],
    });
    const userGroups = result.fields?.[USER_GROUPS_FIELD_NAME] as {
      manyValues?: { values: ProfileValue[] };
    };
    return userGroups?.manyValues?.values || [];
  }

  public async getUserProfile(
    profileName: string,
  ): Promise<ProfileValue | undefined> {
    const result = await this.getProfileData({
      fields: [profileName],
    });
    const profile = result.fields?.[profileName] as {
      aSingleValue?: ProfileValue;
    };
    return profile?.aSingleValue;
  }

  public async getLoginAndSkipTimes(): Promise<{
    lastLogins: number[];
    lastSkip?: number;
  }> {
    try {
      const result = await this.getProfileData({
        fields: [LAST_LOGIN_FIELD, SKIP_2FA_FIELD],
      });
      const loginField = result.fields?.[LAST_LOGIN_FIELD] as
        | { manyValues?: { values: ProfileValue[] } }
        | undefined;
      const lastLogins = (loginField?.manyValues?.values ?? [])
        .map(profileValueToMillis)
        .filter((ms): ms is number => ms !== undefined)
        .sort((a, b) => b - a); // newest first

      const skipField = result.fields?.[SKIP_2FA_FIELD] as
        | { aSingleValue?: ProfileValue }
        | undefined;
      const lastSkip = profileValueToMillis(skipField?.aSingleValue);

      return { lastLogins, lastSkip };
    } catch (e) {
      console.error('Failed to get enforcement eligibility data', e);
      return { lastLogins: [] };
    }
  }

  public handleError(response: any) {
    const errorCode =
      response?.response?.data?.errorCode ||
      response?.data?.errorCode ||
      response?.data?.reason ||
      response?.reason;
    const errorReason = this.errorHandling(errorCode);

    return { success: false, errorCode, errorReason };
  }

  public errorHandling(
    errorCode: number | string,
    defaultMsg = 'ops_something_went_wrong',
  ) {
    const errorCodeStr =
      errorCode && isNumber(errorCode) ? errorCode.toString() : errorCode;
    switch (errorCodeStr) {
      default:
        return defaultMsg;
    }
  }
}
